import {
    sharingIssueSubTitle,
    sharingIssueSubTitleFromCloudyAttachment,
    sharingIssueSubTitleFromLink,
} from './getHasSharingIssues.locstring.json';
import loc, { format } from 'owa-localize';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { InfoBarMessageRank } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { InfoBarCustomAction } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import {
    AnchorElementsSource,
    getSharingLink,
    getSharingDataFromLink,
    lazyGetSharingTipRecipientInfo,
    RecipientContainer,
} from 'owa-link-data';
import { composeGetHasSharingIssues } from 'owa-recipient-permission-checker';
import {
    AttachmentWellViewState,
    lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend,
} from 'owa-attachment-well-data';
import {
    IdType,
    lazyGetSharingIssuesForSharingData,
    lazyGetInfoBarCreator,
    getSharingTipIdsList,
    SharingTipId,
    SharingTipInfo,
    SharingData,
    SharingTipRecipientInfo,
} from 'owa-sharing-data';

const newLinkSources = [
    AnchorElementsSource.PasteHtml,
    AnchorElementsSource.InsertLinkFromToolbar,
    AnchorElementsSource.AutoLink,
    AnchorElementsSource.ShareLinkCompose,
    AnchorElementsSource.InsertLinkFromAttachMenu,
];

let getInfoBarCreator: (
    sharingData: SharingData[],
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    skipActionRecommendation: boolean,
    convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
) => InfoBarMessageViewStateCreator | null;
let getLastCloudyAttachmentsSharingIssueForBlockOnSend: (
    sharingTipIds: SharingTipId[],
    attachmentWellViewState: AttachmentWellViewState,
    recipientInfo: SharingTipRecipientInfo[],
    getInfoBarCreator: (
        sharingData: SharingData[],
        sharingTipId: SharingTipId,
        recipientInfo: SharingTipRecipientInfo[],
        skipActionRecommendation: boolean,
        convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
    ) => InfoBarMessageViewStateCreator | null
) => string;
let getSharingTipRecipientInfo: (
    recipientContainers: RecipientContainer[],
    fromAddress: string
) => SharingTipRecipientInfo[];
let getSharingIssuesForSharingData: (
    sharingData: SharingData,
    recipientInfo: SharingTipRecipientInfo[]
) => SharingTipInfo[];

export async function getHasSharingIssues(
    infoBarIds: string[],
    attachmentWell: AttachmentWellViewState,
    fromAddress: string,
    recipientContainers: RecipientContainer[],
    isCalendar
): Promise<boolean> {
    // log how many links are being sent. We want to do this even if we aren't checking for sharing issues
    const linkIds =
        attachmentWell && attachmentWell.sharingLinkIds ? attachmentWell.sharingLinkIds : [];
    let newLinkCount = 0;
    linkIds.forEach(linkId => {
        const sharingLink = getSharingLink(linkId);
        if (sharingLink && newLinkSources.indexOf(sharingLink.source) !== -1) {
            newLinkCount++;
        }
    });
    logUsage('ValidateSharingIssuesBeforeSend', {
        linkInBodyCount: linkIds.length,
        newLinkCount: newLinkCount,
    });

    // This is only being used for block on send for now, and this will tie its roll out to contextual tips
    // for links in the body. If this method is going to be re-used for something besides block on send
    // we should move this flight check to validateSend.ts and to doAnyLinksHaveSharingIssues
    if (!isFeatureEnabled('doc-attachment-contextualSharingTips')) {
        return false;
    }

    if (!isFeatureEnabled('doc-link-calendar') && isCalendar) {
        return false;
    }

    // If it hasn't been loaded, it means that either there is no sharing issues or it has failed to load.
    // Then we just return false as we don't want to delay the check for load failure.
    if (!lazyGetSharingTipRecipientInfo.isLoaded()) {
        return false;
    }

    composeGetHasSharingIssues(attachmentWell.composeId);
    const sharingTipIds: SharingTipId[] = getSharingTipIdsList();

    const refAttachmentSharingIssues = isCalendar
        ? []
        : infoBarIds.filter(id => (<string[]>sharingTipIds).indexOf(id) !== -1);

    if (!getSharingTipRecipientInfo) {
        getSharingTipRecipientInfo = await lazyGetSharingTipRecipientInfo.import();
    }

    let linkSharingIssues: SharingTipInfo[] = [];
    if (lazyGetSharingIssuesForSharingData.isLoaded()) {
        if (!getSharingIssuesForSharingData) {
            getSharingIssuesForSharingData = await lazyGetSharingIssuesForSharingData.import();
        }

        linkSharingIssues = getLinkSharingIssues(attachmentWell, fromAddress, recipientContainers);
    }
    return refAttachmentSharingIssues.length !== 0 || linkSharingIssues.length !== 0;
}

export async function preloadLazyImports() {
    if (!getLastCloudyAttachmentsSharingIssueForBlockOnSend) {
        getLastCloudyAttachmentsSharingIssueForBlockOnSend = await lazyGetLastCloudyAttachmentsSharingIssueForBlockOnSend.import();
    }

    if (!getInfoBarCreator) {
        getInfoBarCreator = await lazyGetInfoBarCreator.import();
    }
}

function getLinkSharingIssues(
    attachmentWell: AttachmentWellViewState,
    fromAddress: string,
    recipientContainers: RecipientContainer[]
): SharingTipInfo[] {
    let sharingTipsInfos: SharingTipInfo[] = [];
    if (!getSharingTipRecipientInfo || !getSharingIssuesForSharingData) {
        return sharingTipsInfos;
    }
    const linkIds = attachmentWell.sharingLinkIds;

    if (!linkIds || linkIds.length === 0) {
        return sharingTipsInfos;
    }

    const sharingData: SharingData[] = linkIds.map(id => getSharingDataFromLink(id));
    const recipientInfo: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
        recipientContainers,
        fromAddress
    );
    sharingData.forEach(function(data) {
        sharingTipsInfos = sharingTipsInfos.concat(
            getSharingIssuesForSharingData(data, recipientInfo).filter(
                issue => issue.messageRank === InfoBarMessageRank.Error
            )
        );
    });

    return sharingTipsInfos;
}

export function getSharingIssuesString(
    infoBarIds: string[],
    attachmentWell: AttachmentWellViewState,
    fromAddress: string,
    recipientContainers: RecipientContainer[]
): string | null {
    if (!getSharingTipRecipientInfo) {
        logUsage('genericSharingIssueBlockOnSendLazyImportFailure');
        return loc(sharingIssueSubTitle);
    }

    const sharingTipIds: SharingTipId[] = getSharingTipIdsList();
    const refAttachmentSharingIssues = infoBarIds.filter(
        id => (<string[]>sharingTipIds).indexOf(id) !== -1
    );

    const linkSharingIssues: SharingTipInfo[] = getLinkSharingIssues(
        attachmentWell,
        fromAddress,
        recipientContainers
    );

    if (refAttachmentSharingIssues.length + linkSharingIssues.length === 1) {
        if (
            refAttachmentSharingIssues.length === 1 &&
            getInfoBarCreator &&
            getLastCloudyAttachmentsSharingIssueForBlockOnSend
        ) {
            const recipientInfo: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
                recipientContainers,
                fromAddress
            );
            const refAttachmentSharingIssue: string = getLastCloudyAttachmentsSharingIssueForBlockOnSend(
                sharingTipIds,
                attachmentWell,
                recipientInfo,
                getInfoBarCreator
            );
            if (refAttachmentSharingIssue) {
                logUsage('sharingIssueBlockOnSend', {
                    sharingTipId: refAttachmentSharingIssues[0],
                    errorSource: 'attachments',
                });
                return format(
                    loc(sharingIssueSubTitleFromCloudyAttachment),
                    refAttachmentSharingIssue
                );
            }
        } else if (linkSharingIssues.length === 1) {
            logUsage('sharingIssueBlockOnSend', {
                sharingTipId: linkSharingIssues[0].id,
                errorSource: 'links',
            });
            return format(loc(sharingIssueSubTitleFromLink), linkSharingIssues[0].sharingTipString);
        }
    } else if (refAttachmentSharingIssues.length + linkSharingIssues.length > 1) {
        let errorSource: string = 'linksAndAttachments';
        if (refAttachmentSharingIssues.length === 0) {
            errorSource = 'links';
        } else if (linkSharingIssues.length === 0) {
            errorSource = 'attachments';
        }
        logUsage('sharingIssueBlockOnSend', { sharingTipId: 'multiple', errorSource: errorSource });
        return loc(sharingIssueSubTitle);
    }

    return null;
}
